import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './shadcn/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './shadcn/card';
import { AlertCircle } from 'lucide-react';
import { useTenantAuthStore } from '../store/tenantAuthStore';
import { captureException } from '../lib/sentry';
import { ROUTES } from '@/config/routes';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in the child component tree.
 * 
 * Features:
 * - Catches errors during rendering, in lifecycle methods, and in constructors
 * - Displays a user-friendly fallback UI when an error occurs
 * - Logs error details to the console for debugging
 * - Allows users to attempt recovery by resetting the error state
 * - Does not interfere with routing and navigation
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state so the next render will show the fallback UI.
   * This is called during the render phase, so side effects are not allowed.
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  /**
   * Log error information for debugging purposes.
   * This is called during the commit phase, so side effects are allowed.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details to console for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error component stack:', errorInfo.componentStack);

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Report to Sentry in production
    captureException(error, {
      tags: {
        errorBoundary: 'true',
        component: 'ErrorBoundary',
      },
      extra: {
        componentStack: errorInfo.componentStack,
      },
      level: 'error',
    });
  }

  /**
   * Reset the error boundary state, allowing the user to try again.
   * This works for transient errors (race conditions, network issues, etc.)
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Navigate to a safe page based on authentication status.
   * - If authenticated: Navigate to dashboard
   * - If not authenticated: Navigate to home page
   * This prevents infinite loops if the current page/component is broken.
   */
  handleGoToSafePage = (): void => {
    // Access Zustand store directly (class component can't use hooks)
    const isAuthenticated = useTenantAuthStore.getState().isAuthenticated;

    // Redirect based on authentication status
    if (isAuthenticated) {
      window.location.href = ROUTES.DASHBOARD;
    } else {
      window.location.href = ROUTES.HOME;
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background-secondary p-4">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-6 w-6 text-destructive" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
              <CardDescription>
                We encountered an unexpected error. Don't worry, your data is safe.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  The application encountered an error and couldn't continue. You can try again or return to a safe page.
                </p>
                
                {/* Show error details in development mode */}
                {import.meta.env.DEV && this.state.error && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm font-medium text-foreground hover:text-foreground/80">
                      Error Details (Development Only)
                    </summary>
                    <div className="mt-2 p-3 bg-muted rounded-md overflow-auto">
                      <p className="text-xs font-mono text-destructive mb-2">
                        {this.state.error.toString()}
                      </p>
                      {this.state.errorInfo && (
                        <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      )}
                    </div>
                  </details>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button onClick={this.handleReset} variant="default">
                Try Again
              </Button>
              <Button onClick={this.handleGoToSafePage} variant="outline">
                Go to Safe Page
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

